'use client'

import { forwardRef, useState, useRef, useEffect, useImperativeHandle } from "react";
import styles from "./ua.module.scss";
import Button from "@/components/button/Button";
import Icon from "@/components/icon/Icon";
import PanelHeader from "@/components/panel/PanelHeader";
import PanelTitle from "@/components/panel/PanelTitle";
import PanelBody from "@/components/panel/PanelBody";
import Slideout from "@/components/slideout/Slideout";
import StackedPanel from "@/components/panel/StackedPanel";
import Form from "@/components/form/Form";
import Field from "@/components/form/Field";
import FormCells from "@/components/form/FieldCells";
import FormCell from "@/components/form/FieldCell";
import OTPConfirmationForm from "./OTPConfirmationForm";
import EditProfileForm from "./EditProfileForm";
import ApiUtils from "@/utils/ApiUtils";
import useEventListener, { emitEvent } from "@/components/plugins/EventListener";
import { formatCurrency, formatNumber } from "@/utils/Formatters";
import { DM_EVENT_TRADE_ESTIMATE, useDMEventListener } from "@dealermesh/react";
import Disclaimer from "@/components/panel/Disclaimer";
import GoogleLoginButton from "@/components/plugins/GoogleLoginButton";

const ProfileSlideout = forwardRef((props,ref) => {
  
  const [loaded,setLoaded] = useState(false)
  const [user,setUser] = useState(null)
  const [email,setEmail] = useState('')
  const [otp,setOtp] = useState()
  const [editingProfile,setEditingProfile] = useState(false)
  const [trade,setTrade] = useState(props.site.session?.vehicles?.trade)
  
  const slide = useRef()
  
  useEventListener("show_profile_slide", event => {
    loadMe()
    slide.current.toggle()
  })
  
  useDMEventListener(DM_EVENT_TRADE_ESTIMATE, event => {
    if (event.detail?.value) {
      const t = event.detail
      setTrade({
        year: t.vehicle.year,
        make_name: t.vehicle.make_name,
        model_name: t.vehicle.model_name,
        trim_name: t.vehicle.trim_name,
        value_low: t.value_low,
        value_hign: t.value_high,
      })
    }
  })

  useEventListener("third_party_signin", event => {
    const user = event.detail
    if (!event.detail.ctx || event.detail.ctx == "profile_slideout") {
      let data = {}
      data["first_name"] = user.first_name
      data["last_name"] = user.last_name
      data["email"] = user.email
      data["ext_id"] = user.account_id
      data["platform"] = user.platform
      ApiUtils.postRequest("/console/api/users/register", data)
      .then(data => {
        loadMe()
      })
    }
  })

  const toggle = () => {
    slide.current.toggle()
  }

  useEffect(() => {
    if (ApiUtils.authToken()) {
      loadMe()
    } else {
      setLoaded(true)
    }
  }, [])

  const loadMe = () => {
    ApiUtils.getRequest("/console/api/users/me")
    .then(data => {
      setUser(data.user)
      setLoaded(true)
    })
    .catch(err => {
    })
  }
  
  const onEmailChange = (value) => {
    setEmail(value)
  }
  
  const onSubmit = (e) => {
    e.preventDefault()
    ApiUtils.postRequest("/console/api/users/send_otp", { email: email, site: props.site.site_id })
    .then(data => {
      setOtp(data.otp)
    })
  }
  
  const onProfileUpdate = (data) => {
    setUser(Object.assign(user || {}, data.user))
    setEditingProfile(false)
  }

  const onOTPConfirm = (data) => {
    setOtp(null)
    setUser(data.user)
  }

  const showTrade = () => {
    emitEvent("show_trade_slide", { title: trade ? "Update your vehicle" : "Find your vehicle" })
    toggle()
  }
  
  useImperativeHandle(ref, () => ({
    toggle
  }))
  
  return <Slideout ref={slide} style={{width: '420px'}}>
    <StackedPanel>
      <PanelHeader>
        <PanelTitle leftPad={true} centered={true}>
          <h2>{ user ? 'My Profile' : otp ? 'Confirm' : 'Sign In / Sign Up' }</h2>
        </PanelTitle>
        <div>
          <Button theme={'icon'} onClick={e => toggle(e)} label={ 'Close' }>
            <Icon {...props} name="x"></Icon>
          </Button>
        </div>
      </PanelHeader>
      <PanelBody padded={true}>
        <section>
          { loaded && !user && !otp &&
          <Form className={ styles.form } id="profile_menu_signin" name="Sign In Form" onSubmit= {e => onSubmit(e)}>
            <FormCells>
              <FormCell>
                <Field type="text" name="email" placeholder="Email" label="Email" noLabel={true} onChange={value => onEmailChange(value)}></Field>
              </FormCell>
              <FormCell>
                <Button theme={['secondary']} label={ 'Sign In' } type="submit">Continue</Button>
              </FormCell>
              { props.site.api_keys?.g_oauth_client_id &&
              <FormCell>
                <GoogleLoginButton {...props} ctx="profile_slideout"></GoogleLoginButton>
              </FormCell>
              }
              { props.site.profile?.disclaimer &&
              <FormCell>
                <Disclaimer disclaimer={ props.site.profile?.disclaimer }></Disclaimer>
              </FormCell>
              }
              { props.site.profile?.email_disclaimer &&
              <FormCell>
                <Disclaimer disclaimer={ props.site.profile?.email_disclaimer }></Disclaimer>
              </FormCell>
              }
            </FormCells>
          </Form>
          }
          { !user && otp &&
          <OTPConfirmationForm {...props} email={email} params={otp} onConfirm={data => onOTPConfirm(data)}></OTPConfirmationForm>
          }
          { user && !editingProfile &&
          <div className={'flex-column flex-with-gap-lg'}>
            <div className="flex-col">
              <h2 className="text-md text-primary">Welcome { user.first_name || 'Guest' }!</h2>
              <div>{ user.email }</div>
            </div>
            <div className="flex-col">
              <div className="flex-row flex-with-gap">
                <div className="flex-col">
                  <Button theme={['line']} label={'Edit Profile'} style={{ width: '180px' }} onClick={e => setEditingProfile(true)}></Button>
                </div>
              </div>
            </div>
            <div className={styles.divider}></div>
            <div className="flex-col">
              <h2 className="text-md text-primary">Your Vehicle</h2>
            </div>
            { trade &&
            <div className="flex-col">
              <h3 className="text-bold">{ trade.year } { trade.make_name } { trade.model_name } { trade.trim_name }</h3>
              <span className="text-primary">Trade Estimate: { formatCurrency(trade.value_low) } - { formatCurrency(trade.value_high) }</span>
            </div>
            }
            <div className="flex-col">
              <div className="flex-row flex-with-gap">
                <div className="flex-col">
                  <Button theme={['line']} label={trade ? 'Update' : 'Add'} style={{ width: '180px' }} onClick={e => showTrade()}></Button>
                </div>
              </div>
            </div>
          </div>
          }
          { user && editingProfile &&
          <EditProfileForm {...props} user={user} onSubmit={data => onProfileUpdate(data)}></EditProfileForm>
          }
        </section>
      </PanelBody>
    </StackedPanel>
  </Slideout>    
  
})

ProfileSlideout.displayName = "ProfileSlideout"
export default ProfileSlideout